import React, { useMemo, useState, useEffect } from "react";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Form, FormRequest, FormServices, FIELD_TYPE_MAP, UpsertFormRequest } from "@/services/bedrock";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Eye,
  Search,
  Trash,
  Crosshair,
  Settings,
  Route,
  AlertTriangle,
} from "lucide-react";
import { Svg } from "@/assets";
import { Badge } from "@/components/ui/badge";
import { FormDialog } from "./components/FormDialog";
import { DropdownMenu, Input, Tooltip } from "@/components/ui";
import { FormRequestDialog } from "./components/FormRequestDialog";
import { FormCreateAIDialog } from "./components/FormCreateAIDialog";
import { FormRequestsListDialog } from "./components/FormRequestsListDialog";
import { FormResponsesListDialog } from "./components/FormResponsesListDialog";
import { FormEligibilityDialog } from "./components/FormEligibilityDialog";
import { FormDefaultsDialog } from "./components/FormDefaultsDialog";
import { joinLabels, missingAccountDefaults } from "./formDefaults";
import { FormRoutesPage } from "./components/FormRoutes/FormRoutesPage";
import { useHistory, useRouteMatch } from "react-router";
import { kFormatter, URLS } from "@/utils";

/** The Forms tab: the forms list, or the Form Routes page at its own URL. */
export function Forms() {
  const onFormRoutes = !!useRouteMatch({ path: URLS.coach.reputation.formRoutes, exact: true });
  return onFormRoutes ? <FormRoutesPage /> : <FormsList />;
}

function FormsList() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formEligibilityDialogOpen, setFormEligibilityDialogOpen] = useState(false);
  const [formDefaultsDialogOpen, setFormDefaultsDialogOpen] = useState(false);
  const [formCreateAIDialogOpen, setFormCreateAIDialogOpen] = useState(false);
  const [formRequestDialogOpen, setFormRequestDialogOpen] = useState(false);
  const [formRequestsListDialogOpen, setFormRequestsListDialogOpen] = useState(false);
  const [formResponsesListDialogOpen, setFormResponsesListDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<Form | UpsertFormRequest | null>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [selectedFormRequest, setSelectedFormRequest] = useState<FormRequest | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (formId: number) => FormServices.deleteForm(formId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
    },
    onError: err => {
      console.error("Failed to delete form", err);
      alert("Error deleting form. Please try again.");
    },
  });

  // Search States
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input to prevent hammering the API
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);

    return () => clearTimeout(handler);
  }, [searchInput]);

  // Shares the key FormDefaultsDialog invalidates on save, so the warning clears once they're set
  const { data: coachDefaults } = useQuery({
    queryKey: ["formDefaults"],
    queryFn: () => FormServices.getCoachDefaults(),
  });
  const missingDefaults = coachDefaults ? missingAccountDefaults(coachDefaults) : [];

  const { data, isLoading } = useQuery({
    queryKey: ["forms", pagination, debouncedSearch],
    queryFn: () =>
      FormServices.getForms({
        ...pagination,
        search: debouncedSearch || undefined,
      }),
  });

  const columns = useMemo<ColumnDef<Form>[]>(
    () => [
      {
        accessorKey: "displayName",
        header: "Form Name",
        cell: ({ row }) => {
          const form = row.original;
          const fields = form.formFields || [];

          // Only keep nps, star, ten_point, sentiment fieldTypes
          const ratingTypes = fields
            .map(f => f.fieldType)
            .filter((type): type is "nps" | "star" | "ten_point" | "sentiment" =>
              ["nps", "star", "ten_point", "sentiment"].includes(type)
            );

          // De-duplicate types
          const uniqueTypes = Array.from(new Set(ratingTypes));

          return (
            <div className="flex items-center gap-3">
              <span
                className="font-semibold cursor-pointer text-slate-800 hover:text-primary hover:underline select-none"
                onClick={() => {
                  setSelectedForm(form);
                  setFormDialogOpen(true);
                }}
              >
                {form.displayName}
              </span>
              <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                {uniqueTypes.map(type => {
                  const mapping = FIELD_TYPE_MAP[type];
                  if (!mapping) return null;
                  const Icon = mapping.icon;
                  return <Icon key={type} className="size-4 text-muted-foreground" title={mapping.label} />;
                })}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "active",
        header: "Status",
        cell: ({ row }) => (
          <Badge variant={row.original.active ? "default" : "outline"}>
            {row.original.active ? "Published" : "Draft"}
          </Badge>
        ),
      },
      {
        accessorKey: "totalRequestCount",
        header: "Form Links",
        cell: ({ row }) => {
          const form = row.original;
          const count = form.totalRequestCount ?? 0;
          return (
            <button
              type="button"
              className="font-semibold font-mono text-xs text-primary hover:underline cursor-pointer select-none"
              onClick={e => {
                e.stopPropagation();
                setSelectedForm(form);
                setFormRequestsListDialogOpen(true);
              }}
            >
              {kFormatter(count)}
            </button>
          );
        },
      },
      {
        accessorKey: "totalResponseCount",
        header: "Responses",
        cell: ({ row }) => {
          const form = row.original;
          const count = form.totalResponseCount ?? 0;
          return (
            <button
              type="button"
              className="font-semibold font-mono text-xs text-primary hover:underline cursor-pointer select-none"
              onClick={e => {
                e.stopPropagation();
                setSelectedForm(form);
                setFormResponsesListDialogOpen(true);
              }}
            >
              {kFormatter(count)}
            </button>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const form = row.original;
          return (
            <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
              {form.active && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedForm(form);
                    setFormRequestDialogOpen(true);
                  }}
                  className="h-8 py-1.5 px-3 text-xs"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Create Link
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenu.Trigger
                  render={props => (
                    <Button
                      {...props}
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-slate-800"
                    >
                      <span className="sr-only">Actions</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  )}
                />
                <DropdownMenu.Content align="end">
                  <DropdownMenu.Item
                    onClick={() => {
                      setSelectedForm(form);
                      setFormDialogOpen(true);
                    }}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    <span>Edit Form</span>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onClick={() => {
                      setSelectedForm(form);
                      setFormEligibilityDialogOpen(true);
                    }}
                  >
                    <Crosshair className="mr-2 h-4 w-4" />
                    <span>Edit Eligibility</span>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onClick={() => {
                      setSelectedForm(form);
                      setFormResponsesListDialogOpen(true);
                    }}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    <span>See Responses</span>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onClick={() => {
                      if (
                        confirm(
                          `Are you sure you want to delete "${form.displayName}"? This action cannot be undone and will delete all associated form requests.`
                        )
                      ) {
                        deleteMutation.mutate(form.id);
                      }
                    }}
                    className="text-destructive focus:text-destructive focus:bg-destructive/10"
                  >
                    <Trash className="mr-2 h-4 w-4 text-destructive" />
                    <span className="text-destructive font-medium">Delete Form</span>
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    []
  );

  const isTrulyEmpty = !isLoading && data?.content.length === 0 && !debouncedSearch;
  const isSearchEmpty = !isLoading && data?.content.length === 0 && debouncedSearch;

  const handleCreateClick = () => {
    setSelectedForm(null);
    setFormCreateAIDialogOpen(true);
  };

  const handleCreateAISuccess = (draft: UpsertFormRequest) => {
    setFormCreateAIDialogOpen(false);
    setSelectedForm(draft);
    setFormDialogOpen(true);
  };

  return (
    <div className="flex flex-col gap-4">
      {isTrulyEmpty ? (
        <FormsEmptyState onCreateClick={handleCreateClick} />
      ) : (
        <>
          {/* Top Search & Actions Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 select-none">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search forms by name..."
                className="bg-white pl-9 text-xs"
              />
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {missingDefaults.length ? (
                <Tooltip>
                  <Tooltip.Trigger
                    render={
                      <Button
                        variant="outline"
                        onClick={() => setFormDefaultsDialogOpen(true)}
                        disabled={isLoading}
                        className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                      >
                        <AlertTriangle className="mr-2 h-4 w-4 text-amber-600" />
                        Manage Defaults
                      </Button>
                    }
                  />
                  <Tooltip.Content className="max-w-xs whitespace-normal">
                    No account default for {joinLabels(missingDefaults.map(d => d.label))}. Set one so every player and
                    link has a form to open.
                  </Tooltip.Content>
                </Tooltip>
              ) : (
                <Button variant="outline" onClick={() => setFormDefaultsDialogOpen(true)} disabled={isLoading}>
                  <Settings className="mr-2 h-4 w-4" />
                  Manage Defaults
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => history.push(URLS.coach.reputation.formRoutes)}
                disabled={isLoading}
              >
                <Route className="mr-2 h-4 w-4" />
                Form Routes
              </Button>
              <Button onClick={handleCreateClick} disabled={isLoading}>
                <Plus className="mr-2 h-4 w-4" />
                Create Form
              </Button>
            </div>
          </div>

          {isSearchEmpty ? (
            <FormsSearchEmptyState searchInput={searchInput} onClear={() => setSearchInput("")} />
          ) : (
            <DataTable
              columns={columns}
              data={data?.content ?? []}
              loading={isLoading}
              pagination={pagination}
              onPaginationChange={setPagination}
              pageCount={data?.pagination?.totalPages}
              rowCount={data?.pagination?.totalCount}
            />
          )}
        </>
      )}

      <FormDialog
        open={formDialogOpen}
        onOpenChange={open => {
          setFormDialogOpen(open);
          if (!open) {
            setSelectedForm(null);
          }
        }}
        form={selectedForm}
        onCreateRequestClick={createdForm => {
          setFormDialogOpen(false);
          setSelectedForm(createdForm);
          setFormRequestDialogOpen(true);
        }}
      />

      <FormEligibilityDialog
        open={formEligibilityDialogOpen}
        onOpenChange={setFormEligibilityDialogOpen}
        formId={(selectedForm as Form)?.id}
      />

      <FormDefaultsDialog open={formDefaultsDialogOpen} onOpenChange={setFormDefaultsDialogOpen} />

      <FormCreateAIDialog
        open={formCreateAIDialogOpen}
        onOpenChange={setFormCreateAIDialogOpen}
        onSuccess={handleCreateAISuccess}
      />

      <FormRequestDialog
        open={formRequestDialogOpen}
        onOpenChange={open => {
          setFormRequestDialogOpen(open);
          if (!open) {
            setSelectedForm(null);
            setSelectedFormRequest(null);
          }
        }}
        form={selectedForm as any}
        formRequest={selectedFormRequest}
      />

      <FormRequestsListDialog
        open={formRequestsListDialogOpen}
        onOpenChange={open => {
          setFormRequestsListDialogOpen(open);
          if (!open) {
            setSelectedForm(null);
          }
        }}
        form={selectedForm as any}
        onSeeResponses={(form, requestId) => {
          setFormRequestsListDialogOpen(false);
          setSelectedForm(form);
          setSelectedRequestId(requestId);
          setFormResponsesListDialogOpen(true);
        }}
        onEditRequest={req => {
          setFormRequestsListDialogOpen(false);
          setSelectedForm(selectedForm as Form);
          setSelectedFormRequest(req);
          setFormRequestDialogOpen(true);
        }}
      />

      <FormResponsesListDialog
        open={formResponsesListDialogOpen}
        onOpenChange={open => {
          setFormResponsesListDialogOpen(open);
          if (!open) {
            setSelectedForm(null);
            setSelectedRequestId(null);
          }
        }}
        form={selectedForm as any}
        formRequestId={selectedRequestId}
      />
    </div>
  );
}

function FormsEmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-12 text-center bg-card shadow-sm">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6 text-primary">
        <img src={Svg.Library} alt="Forms" className="h-10 w-10" />
      </div>
      <h3 className="text-2xl font-bold tracking-tight mb-2">Manage Your Feedback & Surveys</h3>
      <p className="text-muted-foreground max-w-md mx-auto mb-8">
        Create and manage feedback forms and surveys for lead generation, NPS, general feedback, and first or
        third-party reviews.
      </p>
      <Button size="lg" onClick={onCreateClick}>
        <Plus className="mr-2 h-5 w-5" />
        Create your first form
      </Button>
    </div>
  );
}

function FormsSearchEmptyState({ searchInput, onClear }: { searchInput: string; onClear: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 p-12 text-center bg-white shadow-sm select-none animate-in fade-in duration-300">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 mb-4 text-slate-400">
        <Search className="h-6 w-6" />
      </div>
      <h3 className="text-base font-bold text-slate-800 mb-1 font-sans">No results found</h3>
      <p className="text-xs text-muted-foreground max-w-xs mx-auto mb-4 font-sans">
        We couldn't find any forms matching "{searchInput}". Try adjusting your spelling or keywords.
      </p>
      <Button variant="outline" size="sm" onClick={onClear}>
        Clear Search Filter
      </Button>
    </div>
  );
}
